/**
 * HTML -> plain text (engine.md §2), ported from v4 `src/lib/html-text.ts` onto htmlparser2 (isomorphic,
 * no DOM, full entity decoding). Deterministic: excerpt, FTS, the matcher, Jev and the Markdown export
 * all use it, so its output must never depend on the runtime.
 *
 *   - script / style / noscript / template / svg / math (and their content) are dropped;
 *   - block elements end with a paragraph break, <br> is a newline, <li> starts with '• ', table cells
 *     are separated by a space, other inline elements add nothing ("<b>Hel</b>lo" -> "Hello");
 *   - entities are decoded; everything else is stripped;
 *   - inline whitespace collapses, blank-line runs are capped at one, the result is trimmed.
 *
 * Input that does not look like HTML (no element tag, no comment) is only entity-decoded and
 * whitespace-normalised, so a bare "x < 10" in a plain-text body survives (v4 behaviour).
 */

import { Parser } from 'htmlparser2';

const DROPPED = new Set(['script', 'style', 'noscript', 'template', 'svg', 'math', 'head', 'title']);
const BLOCKS = new Set([
  'p', 'div', 'li', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'section', 'article', 'ul', 'ol',
  'table', 'thead', 'tbody', 'tfoot', 'pre', 'figure', 'figcaption', 'header', 'footer', 'aside', 'nav', 'main',
  'dl', 'dt', 'dd', 'hr', 'address', 'details', 'summary',
]);

/** Table cells separate their text; other inline elements add nothing (like rendered text). */
const CELLS = new Set(['td', 'th']);

function looksLikeHtml(s: string): boolean {
  return /<\/?[a-z][a-z0-9]*(\s[^>]*)?\/?>/i.test(s) || s.includes('<!--');
}

function normaliseWhitespace(s: string): string {
  return s
    .replace(/ /g, ' ')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Decode entities of a plain-text string through the same decoder the parser uses. */
function decodeOnly(s: string): string {
  let out = '';
  const parser = new Parser({ ontext: (t) => { out += t; } }, { decodeEntities: true });
  // Escape '<' so nothing is read as markup; entities are still decoded.
  parser.write(s.replace(/</g, '&lt;'));
  parser.end();
  return out;
}

export function htmlToText(html: string | null | undefined): string {
  if (!html) return '';
  if (!looksLikeHtml(html)) return normaliseWhitespace(decodeOnly(html));
  let out = '';
  let dropDepth = 0;
  const parser = new Parser(
    {
      onopentag(name) {
        if (DROPPED.has(name)) { dropDepth++; return; }
        if (dropDepth > 0) return;
        if (name === 'br') out += '\n';
        else if (name === 'li') out += '\n• ';
        else if (BLOCKS.has(name)) out += '\n\n';
        else if (CELLS.has(name)) out += ' ';
      },
      onclosetag(name) {
        if (DROPPED.has(name)) { dropDepth = Math.max(0, dropDepth - 1); return; }
        if (dropDepth > 0) return;
        if (BLOCKS.has(name)) out += '\n\n';
        else if (CELLS.has(name)) out += ' ';
      },
      ontext(text) {
        if (dropDepth === 0) out += text;
      },
    },
    { decodeEntities: true, lowerCaseTags: true, recognizeSelfClosing: true },
  );
  parser.write(html);
  parser.end();
  return normaliseWhitespace(out);
}

/**
 * The first absolute http(s) image URL in an HTML fragment (ported from v4): an <img src>, else an <a href>
 * to an image file or a Reddit image host. Entities are decoded.
 */
export function firstImageUrl(html: string | null | undefined): string | null {
  if (!html) return null;
  let img: string | null = null;
  let link: string | null = null;
  const parser = new Parser(
    {
      onopentag(name, attribs) {
        if (img) return;
        if (name === 'img' && attribs.src) img = attribs.src.trim();
        else if (!link && name === 'a' && attribs.href) {
          const href = attribs.href.trim();
          if (/^https?:\/\/[^\s]+\.(?:jpe?g|png|gif|webp)(?:\?\S*)?$/i.test(href)
            || /^https?:\/\/(?:i|preview|external-preview)\.redd\.it\//i.test(href)) link = href;
        }
      },
    },
    { decodeEntities: true, lowerCaseTags: true, lowerCaseAttributeNames: true },
  );
  parser.write(html);
  parser.end();
  const url: string | null = img ?? link;
  return url && /^https?:\/\//i.test(url) ? url : null;
}
